// Packages
import React, { useState } from "react"
import styled from "styled-components"
import { Button } from "@gray-fox-components"
import { graphql, useStaticQuery } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"

// Components
import Calculator from "@gray-fox-components/components/calculator/calculator"
import Modal from "@gray-fox-components/components/modal"

// Utils
import { getMediaQueries } from "@gray-fox-components/utils/utils"

// Images
import BackgroundPattern from "@gray-fox-components/images/calculator/background-pattern.svg"

// Styles
const { above } = getMediaQueries()
const StyledComponentContainer = styled.div`
  ${above.desktop`
    padding-block: 60px;
  `}
`
const StyledMainContent = styled.div`
  background-image: url(${BackgroundPattern});
  background-color: var(--primary-lights-2-hex);
  background-position: right top;
  background-repeat: repeat-y;
  display: flex;
  flex-direction: column;
  padding-block: 50px;
  row-gap: 36px;
  .gatsby-image-wrapper {
    margin-inline: auto;
    width: 292px;
    &,
    img {
      border-radius: 2px;
      flex-shrink: 0;
    }
  }
  ${above.mobile`
    column-gap: 52px;
    flex-direction: row;
    padding-inline: 48px;
  `}
  ${above.tablet`
    padding: 60px 144px;
  `}
  ${above.desktop`
    column-gap: 36px;
    padding: 100px 118px;
  `}
`
const StyledHeader = styled.div`
  button {
    width: 100%;
    ${above.mobile`
      width: auto;
    `}
  }
  .description {
    margin-bottom: 40px;
  }

  .headline {
    font-size: 34px;
    font-weight: 700;
    line-height: 50px;
    margin-bottom: 10px;
  }
`

// Main Component
function CalculatorPanel(props) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const data = useStaticQuery(graphql`
    query {
      CalculatorImage: file(
        relativePath: { regex: "/calculator/calculator/" }
        sourceInstanceName: { eq: "gray-fox-images" }
      ) {
        childImageSharp {
          gatsbyImageData
        }
      }
    }
  `)
  const dprLink = props.bankerData
    ? `https://www.workforce-resource.com/dpr/lopmt/GFM?LO=${
        props.bankerData.email
      }&phone=${props.bankerData.phoneTextWithoutExtension
        .replace("(", "")
        .replace(/\s/, "")
        .replace(")", "-")}${
        props.bankerData.name && props.bankerData.nmls
          ? `&name=${props.bankerData.name}&nmlsid=${props.bankerData.nmls}`
          : ``
      }&bcc=webteam@atlanticbay.com`
    : ``
  return (
    <>
      <StyledComponentContainer>
        <StyledMainContent className="layout__main-content layout__padding-container">
          <StyledHeader>
            <h4 className="headline">How Much Can I Afford?</h4>
            <p className="description layout__body--regular">
              Navigating the housing market starts with understanding your
              budget. Start your journey with confidence using our tailored
              mortgage calculator.
            </p>
            <Button
              text="Use our calculator"
              type="primary"
              functions={{
                onClick: () => setIsModalOpen(true),
              }}
            />
            {props.bankerData?.email &&
              props.bankerData?.phoneTextWithoutExtension && (
                <a
                  className="bg-[#E05118] flex gap-[21px] items-center mt-[27px] [padding:7px_9px] rounded-[4px] w-fit hover:no-underline"
                  href={dprLink}
                  rel="noreferrer"
                  target="_blank"
                >
                  {/* Cash Payment Coin */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="35"
                    height="35"
                    viewBox="0 0 35 35"
                    fill="none"
                  >
                    <g clipPath="url(#clip0_2724_3374)">
                      <path
                        d="M23.4348 26.9369L29.8952 24.7917C30.686 24.5572 31.5297 24.5713 32.3122 24.8322C33.0948 25.093 33.7782 25.5879 34.2702 26.25C16.4362 35.1648 22.2068 35.1123 6.56185 29.8958L0.728516 33.5417"
                        stroke="white"
                        strokeWidth="1.45833"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M13.8535 27.7083H21.8743C22.0877 27.7466 22.3068 27.7377 22.5163 27.6821C22.7258 27.6265 22.9206 27.5256 23.0869 27.3865C23.2531 27.2475 23.3869 27.0736 23.4786 26.8772C23.5704 26.6809 23.6179 26.4668 23.6179 26.25C23.6179 26.0332 23.5704 25.8191 23.4786 25.6228C23.3869 25.4264 23.2531 25.2525 23.0869 25.1135C22.9206 24.9744 22.7258 24.8735 22.5163 24.8179C22.3068 24.7623 22.0877 24.7534 21.8743 24.7917H17.4993C15.7708 23.0082 13.419 21.963 10.9368 21.875H6.56185L0.728516 24.7917"
                        stroke="white"
                        strokeWidth="1.45833"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M13.6426 10.4257C13.6426 11.6033 13.8745 12.7693 14.3252 13.8573C14.7758 14.9453 15.4363 15.9338 16.269 16.7665C17.1017 17.5992 18.0903 18.2597 19.1782 18.7104C20.2662 19.161 21.4323 19.393 22.6099 19.393C23.7875 19.393 24.9535 19.161 26.0415 18.7104C27.1295 18.2597 28.118 17.5992 28.9507 16.7665C29.7834 15.9338 30.4439 14.9453 30.8946 13.8573C31.3452 12.7693 31.5772 11.6033 31.5772 10.4257C31.5772 8.04739 30.6324 5.76653 28.9507 4.08483C27.269 2.40314 24.9881 1.45837 22.6099 1.45837C20.2316 1.45837 17.9507 2.40314 16.269 4.08483C14.5873 5.76653 13.6426 8.04739 13.6426 10.4257Z"
                        stroke="white"
                        strokeWidth="1.45833"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M22.6094 5.83333V4.375"
                        stroke="white"
                        strokeWidth="1.45833"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M24.7916 5.83337H21.8327C21.3783 5.83369 20.9382 5.9921 20.5878 6.28142C20.2375 6.57075 19.9987 6.97296 19.9125 7.41908C19.8263 7.86519 19.898 8.32741 20.1153 8.72645C20.3326 9.12548 20.682 9.43648 21.1035 9.60608L24.1135 10.8107C24.535 10.9803 24.8844 11.2913 25.1017 11.6903C25.319 12.0893 25.3907 12.5516 25.3045 12.9977C25.2183 13.4438 24.9795 13.846 24.6292 14.1353C24.2788 14.4247 23.8387 14.5831 23.3843 14.5834H20.4166"
                        stroke="white"
                        strokeWidth="1.45833"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M22.6094 16.0417V14.5834"
                        stroke="white"
                        strokeWidth="1.45833"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_2724_3374">
                        <rect width="35" height="35" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <div className="flex flex-col **:text-white">
                    <span className="font-bold leading-[normal] text-[18px]">
                      Down Payment Assistance May Be Available
                    </span>
                    <span className="leading-[normal] text-[16px]">
                      Click Here to View Programs
                    </span>
                  </div>
                </a>
              )}
          </StyledHeader>
          <GatsbyImage
            image={data.CalculatorImage.childImageSharp.gatsbyImageData}
            alt="mortgage calculator"
          />
        </StyledMainContent>
      </StyledComponentContainer>
      <Modal isModalOpen={isModalOpen}>
        <Calculator closeModal={() => setIsModalOpen(false)} />
      </Modal>
    </>
  )
}

export default CalculatorPanel
